/**
 * Контакты — копия legacy main_contacts.tpl.
 * Для каждого контакта: название (h3), комментарий, подпись типа
 * (Телефон: / Адрес: / Email: / Сайт:) и значение ссылкой, между записями <hr>.
 * Для type='address' — блок .y_map с картой Яндекса; legacy Main::contacts подключал
 * //api-maps.yandex.ru/2.1/?lang=ru_RU и в шаблоне инлайном создавал ymaps.Map +
 * геокодирование «Санкт-Петербург, {адрес}» с центром [59.944732, 30.273672], zoom 11.
 */

import { Fragment, useEffect } from 'react'

import { useExternalScript } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

const YMAPS_SRC = 'https://api-maps.yandex.ru/2.1/?lang=ru_RU'

/** Строка таблицы contacts. type: phone | address | email | link | text. */
interface Contact {
  id: number
  name: string | null
  comment: string | null
  value: string | null
  type: string
}

// Минимальные типы используемой части API Яндекс.Карт 2.1.
interface YGeoObject {
  properties: { get: (key: string) => unknown }
}
interface YGeocodeResult {
  geoObjects: { get: (index: number) => YGeoObject }
}
interface YGeocodePromise {
  then: (onDone: (result: YGeocodeResult) => void) => void
}
interface YMapInstance {
  geoObjects: { add: (object: YGeoObject) => void }
  setBounds: (bounds: unknown, options: { checkZoomRange: boolean }) => void
  destroy: () => void
}
interface YMaps {
  ready: (callback: () => void) => void
  Map: new (
    container: HTMLElement | string,
    options: { center: number[]; zoom: number },
  ) => YMapInstance
  geocode: (request: string, options: { results: number }) => YGeocodePromise
}

declare global {
  interface Window {
    ymaps?: YMaps
  }
}

/** Подпись перед значением — как в шаблоне. */
function typeLabel(type: string): string {
  if (type === 'phone') return 'Телефон: '
  if (type === 'address') return 'Адрес: '
  if (type === 'email') return 'Email: '
  if (type === 'link') return 'Сайт: '
  return ''
}

export function ContactsPage() {
  const { data } = useApi<Contact[]>('/api/content/contacts')
  const contacts = data ?? []
  const mapsReady = useExternalScript(YMAPS_SRC)

  // ключ-строка: эффект перезапускается только при смене набора адресов
  const addressKey = JSON.stringify(
    contacts
      .filter((contact) => contact.type === 'address')
      .map((contact) => ({ id: contact.id, value: contact.value ?? '' })),
  )

  useEffect(() => {
    const ymaps = window.ymaps
    const addresses = JSON.parse(addressKey) as { id: number; value: string }[]
    if (!mapsReady || ymaps === undefined || addresses.length === 0) return

    const maps: YMapInstance[] = []

    ymaps.ready(() => {
      for (const address of addresses) {
        const container = document.getElementById(`map_${address.id}`)
        if (container === null) continue

        const map = new ymaps.Map(container, { center: [59.944732, 30.273672], zoom: 11 })
        maps.push(map)

        ymaps.geocode(`Санкт-Петербург, ${address.value}`, { results: 1 }).then((result) => {
          const firstGeoObject = result.geoObjects.get(0)
          const bounds = firstGeoObject.properties.get('boundedBy')
          map.geoObjects.add(firstGeoObject)
          map.setBounds(bounds, { checkZoomRange: true })
        })
      }
    })

    return () => {
      for (const map of maps) map.destroy()
    }
  }, [mapsReady, addressKey])

  return (
    <div>
      {contacts.map((contact, index) => {
        const name = contact.name ?? ''
        const comment = contact.comment ?? ''
        const value = contact.value ?? ''

        return (
          <Fragment key={contact.id}>
            {name !== '' && <h3>{name}</h3>}
            {comment !== '' && (
              <>
                <span dangerouslySetInnerHTML={{ __html: comment }} />
                <br />
              </>
            )}
            {value !== '' && (
              <>
                {typeLabel(contact.type)}
                {contact.type === 'phone' && (
                  <a href={`tel:${value.replace(/[^0-9+]/g, '')}`}>{value}</a>
                )}
                {contact.type === 'email' && <a href={`mailto:${value}`}>{value}</a>}
                {contact.type === 'link' && (
                  <a target="_blank" rel="noreferrer" href={`http://${value}`}>
                    {value}
                  </a>
                )}
                {contact.type !== 'phone' &&
                  contact.type !== 'email' &&
                  contact.type !== 'link' &&
                  value}
                <br />
              </>
            )}
            {contact.type === 'address' && <div id={`map_${contact.id}`} className="y_map" />}
            {index < contacts.length - 1 && <hr />}
          </Fragment>
        )
      })}
    </div>
  )
}
